import { useState, useEffect, useCallback, useRef } from 'react'

interface UseSlideshowOptions {
  totalSlides: number
  autoPlay?: boolean
  interval?: number
  pauseOnHover?: boolean
}

export function useSlideshow({
  totalSlides,
  autoPlay = true,
  interval = 3000,
  pauseOnHover = true
}: UseSlideshowOptions) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides)
  }, [totalSlides])

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides)
  }, [totalSlides])

  const goToSlide = useCallback((index: number) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlide(index)
    }
  }, [totalSlides])

  const pause = useCallback(() => setIsPaused(true), [])
  const resume = useCallback(() => setIsPaused(false), [])

  useEffect(() => {
    if (!autoPlay || isPaused || totalSlides <= 1) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    timerRef.current = setInterval(nextSlide, interval)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [autoPlay, isPaused, interval, nextSlide, totalSlides])

  const hoverHandlers = pauseOnHover
    ? { onMouseEnter: pause, onMouseLeave: resume }
    : {}

  return {
    currentSlide,
    nextSlide,
    prevSlide,
    goToSlide,
    pause,
    resume,
    isPaused,
    hoverHandlers
  }
}
